import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";
import { difference, get, isEmpty, last, values, without } from "lodash";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Helmet } from "react-helmet";
import { RouteComponentProps } from "react-router-dom";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis
} from "recharts";
import ExternalLink from "../../../components/ExternalLink";
import InfoIcon from "../../../components/icons/InfoIcon";
import { FilteredListProvider } from "../../../context/filteredList";
import { useGoogleAnalytics } from "../../../context/googleAnalytics";
import AppShell from "../../../layout/AppShell";
import classNames from "../../../utils/classNames";
import formatLargeNumber from "../../../utils/formatLargeNumber";
import { BONES_PER_HNT } from "../../../utils/useHntMarket";

interface Props extends RouteComponentProps {}

const Calculate5GRewards: React.FC<Props> = (props) => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number[]>([]);
  const [fiveGMetrics, setFiveGMetrics] = useState<FiveGMetrics>({
    citiesCount: [],
    count: [],
    indoorCount: [],
    outdoorCount: [],
    statesCount: []
  });
  const calculatorRef = useRef<HTMLDivElement>(null);
  const [radioCount, setRadioCount] = useState<RadioCount>({
    indoor: {
      quantity: 1,
      price: 999
    },
    outdoor: {
      quantity: 0,
      price: 1599
    },
    highOutdoor: {
      quantity: 0,
      price: 4999
    }
  });
  const [tokenPrice, setTokenPrice] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [showRoi, setShowRoi] = useState(false);
  const { trackEvent } = useGoogleAnalytics();
  const containerProductsRef = useRef<HTMLDivElement>(null);
  const [filteredLabels, setFilteredLabels] = useState<Label[]>([]);
  const [mobileTools, setMobileTools] = useState<MobileTools>({
    total: 0,
    total_tx: 0,
    wallets_count: 0,
    payment_hashes: []
  });
  const [speedTestSelected, setSpeedTestSelected] =
    useState<string>("Acceptable");

  const unlockResults = useCallback(() => {
    setShowResults((prevState) => !prevState);
    trackEvent("simulate $MOBILE rewards", {
      event_category: "$MOBILE rewards simulation"
    });
  }, [trackEvent]);

  const toggleShowROI = useCallback(() => {
    setShowRoi((prevValue) => !prevValue);

    if (!showRoi) {
      trackEvent("toggle roi simulation for $MOBILE rewards", {
        event_category: "$MOBILE rewards simulation"
      });
    }
  }, [showRoi, trackEvent]);

  const fetch5GMetrics = useCallback(async () => {
    try {
      const response = await fetch(
        "https://explorer-api.helium.com/api/metrics/cells"
      );
      const data: FiveGMetrics = await response.json();

      setFiveGMetrics(data);
    } catch {}
  }, []);

  const fetchMobileTools = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ETL_API}/tools/subtoken/mobile`
      );
      const data: { data: MobileTools } = await response.json();

      setMobileTools(data.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetch5GMetrics();
    fetchMobileTools();
  }, [fetch5GMetrics, fetchMobileTools]);

  const radiosChartData = useMemo(() => {
    let data: {
      [key: string]: {
        Indoor: number;
        Outdoor: number;
        "High-Gain Outdoor": number;
        name: string;
      };
    } = {};

    fiveGMetrics.indoorCount.forEach((item) => {
      const key = item.timestamp.toString();

      data = {
        ...data,
        [key]: {
          Indoor: item.value,
          Outdoor: 0,
          "High-Gain Outdoor": HIGH_POWER_OURDOUR_RADIO_COUNT,
          name: moment(item.timestamp).format("DD MMM")
        }
      };
    });

    fiveGMetrics.outdoorCount.forEach((item) => {
      const key = item.timestamp.toString();

      data = {
        ...data,
        [key]: {
          ...data[key],
          Outdoor: item.value - get(data, [key, "High-Gain Outdoor"], 0)
        }
      };
    });

    return values(data);
  }, [fiveGMetrics.indoorCount, fiveGMetrics.outdoorCount]);

  const citiesChartData = useMemo(() => {
    let data: {
      [key: string]: {
        Cities: number;
        name: string;
      };
    } = {};

    fiveGMetrics.citiesCount.forEach((item) => {
      const key = item.timestamp.toString();

      data = {
        ...data,
        [key]: {
          Cities: item.value,
          name: moment(item.timestamp).format("DD MMM")
        }
      };
    });

    return values(data);
  }, [fiveGMetrics.citiesCount]);

  const calculatedMobileRewardsPerDay = useMemo(() => {
    const rewardsSpeedIndoor =
      REWARD_WEIGHT_INDOOR_RADIO * REWARDS_SPEED_TEST[speedTestSelected];
    const rewardsSpeedOutdoor =
      REWARD_WEIGHT_OUTDOOR_RADIO * REWARDS_SPEED_TEST[speedTestSelected];
    const rewardsSpeedHighOutdoor =
      REWARD_WEIGHT_HIGH_POWER_OUTDOOR_RADIO *
      REWARDS_SPEED_TEST[speedTestSelected];

    const newRewardUnitsPerEpoch =
      radioCount.highOutdoor.quantity * rewardsSpeedHighOutdoor +
      radioCount.outdoor.quantity * rewardsSpeedOutdoor +
      radioCount.indoor.quantity * rewardsSpeedIndoor;

    const currentRewardUnitsPerEpoch =
      HIGH_POWER_OURDOUR_RADIO_COUNT * REWARD_WEIGHT_HIGH_POWER_OUTDOOR_RADIO +
      (getLastValue(fiveGMetrics.outdoorCount) -
        HIGH_POWER_OURDOUR_RADIO_COUNT) *
        REWARD_WEIGHT_OUTDOOR_RADIO +
      getLastValue(fiveGMetrics.indoorCount) * REWARD_WEIGHT_INDOOR_RADIO;

    const totalRewardUnitsPerEpoch =
      newRewardUnitsPerEpoch + currentRewardUnitsPerEpoch;

    return (
      (newRewardUnitsPerEpoch / totalRewardUnitsPerEpoch) * DAILY_MOBILE_REWARDS
    );
  }, [
    fiveGMetrics.indoorCount,
    fiveGMetrics.outdoorCount,
    radioCount.highOutdoor.quantity,
    radioCount.indoor.quantity,
    radioCount.outdoor.quantity,
    speedTestSelected
  ]);

  const mobileMiningEstimate: CardStats[] = useMemo(
    () => [
      {
        title: "Daily",
        value:
          showResults && calculatedMobileRewardsPerDay
            ? formatNumber(calculatedMobileRewardsPerDay)
            : "-",
        description: "$MOBILE per day",
        type: "day"
      },
      {
        title: "Weekly",
        value:
          showResults && calculatedMobileRewardsPerDay
            ? formatNumber(calculatedMobileRewardsPerDay * 7)
            : "-",
        description: "$MOBILE per week",
        type: "week"
      },
      {
        title: "Monthly",
        value:
          showResults && calculatedMobileRewardsPerDay
            ? formatNumber(calculatedMobileRewardsPerDay * 31)
            : "-",
        description: "$MOBILE per month",
        type: "month"
      }
    ],
    [calculatedMobileRewardsPerDay, showResults]
  );

  const helium5GStats: CardStats[] = useMemo(
    () => [
      {
        title: "Total 5G Radios",
        value: formatNumber(getLastValue(fiveGMetrics.count))
      },
      {
        title: "Cities",
        value: formatNumber(getLastValue(fiveGMetrics.citiesCount)),
        tooltip: "Cities with Helium 5G radios in the USA."
      },
      {
        title: "States",
        value: `${formatNumber(getLastValue(fiveGMetrics.statesCount))}/50`,
        tooltip: "States with Helium 5G radios in the USA."
      },
      {
        title: "Indoor Radios",
        value: formatNumber(getLastValue(fiveGMetrics.indoorCount)),
        tooltip: "MosoLabs Indoor & Baicells Neutrino 430H"
      },
      {
        title: "Outdoor Radios",
        value: formatNumber(
          Math.max(
            getLastValue(fiveGMetrics.outdoorCount) -
              HIGH_POWER_OURDOUR_RADIO_COUNT,
            0
          )
        ),
        tooltip: "Baicells Nova 430i, MosoLabs Outdoor & Baicells Nova430H"
      },
      {
        title: "High-Power Outdoor Radios",
        value: formatNumber(HIGH_POWER_OURDOUR_RADIO_COUNT),
        tooltip: "Baicells Nova 436H"
      },
      {
        title: "Paid $MOBILE Rewards",
        value: `${formatLargeNumber(mobileTools.total / BONES_PER_HNT)} MOBILE`,
        tooltip: "Total $MOBILE rewards paid to owners of connected 5G radios."
      },
      {
        title: "$MOBILE Genesis Transactions",
        value: mobileTools.payment_hashes.length.toLocaleString(),
        tooltip: "Total number of $MOBILE genesis reward payout transactions."
      },
      {
        title: "Genesis Payout Wallets",
        value: mobileTools.wallets_count.toLocaleString(),
        tooltip: "Unique wallets that received $MOBILE genesis reward payouts."
      }
    ],
    [
      fiveGMetrics.citiesCount,
      fiveGMetrics.count,
      fiveGMetrics.indoorCount,
      fiveGMetrics.outdoorCount,
      fiveGMetrics.statesCount,
      mobileTools.payment_hashes.length,
      mobileTools.total,
      mobileTools.wallets_count
    ]
  );

  const radioMiningCalculate: RadioMiningCalculate[] = useMemo(
    () => [
      {
        title: "Indoor Radio",
        description:
          "Add the quantity of Indoor Radios you want to simulate and the price you paid for each of them.",
        quantity: radioCount.indoor.quantity,
        setQuantity: (value: number) =>
          setRadioCount((prevState) => ({
            ...prevState,
            indoor: { ...prevState.indoor, quantity: value }
          })),
        price: radioCount.indoor.price,
        setPrice: (value: number) =>
          setRadioCount((prevState) => ({
            ...prevState,
            indoor: { ...prevState.indoor, price: value }
          })),
        rewardWeight: REWARD_WEIGHT_INDOOR_RADIO
      },
      {
        title: "Outdoor Radio",
        description:
          "Add the quantity of Outdoor Radios you want to simulate and the price you paid for each of them.",
        quantity: radioCount.outdoor.quantity,
        setQuantity: (value: number) =>
          setRadioCount((prevState) => ({
            ...prevState,
            outdoor: { ...prevState.outdoor, quantity: value }
          })),
        price: radioCount.outdoor.price,
        setPrice: (value: number) =>
          setRadioCount((prevState) => ({
            ...prevState,
            outdoor: { ...prevState.outdoor, price: value }
          })),
        rewardWeight: REWARD_WEIGHT_OUTDOOR_RADIO
      },
      {
        title: "High-Gain Outdoor Radio",
        description:
          "Add the quantity of High-Gain Radios you want to simulate and the price you paid for each of them.",
        quantity: radioCount.highOutdoor.quantity,
        setQuantity: (value: number) =>
          setRadioCount((prevState) => ({
            ...prevState,
            highOutdoor: { ...prevState.highOutdoor, quantity: value }
          })),
        price: radioCount.highOutdoor.price,
        setPrice: (value: number) =>
          setRadioCount((prevState) => ({
            ...prevState,
            highOutdoor: { ...prevState.highOutdoor, price: value }
          })),
        rewardWeight: REWARD_WEIGHT_HIGH_POWER_OUTDOOR_RADIO
      }
    ],
    [
      radioCount.highOutdoor.price,
      radioCount.highOutdoor.quantity,
      radioCount.indoor.price,
      radioCount.indoor.quantity,
      radioCount.outdoor.price,
      radioCount.outdoor.quantity
    ]
  );

  const speedTestTier = useMemo(
    () => [
      {
        title: "Acceptable",
        labels: ["Download: 100Mb+", "Upload: 10Mb+", "Latency: <50"],
        rewardWeight: REWARDS_SPEED_TEST.Acceptable
      },
      {
        title: "Degraded",
        labels: ["Download: 50Mb+", "Upload: 5Mb+", "Latency: <75"],
        rewardWeight: REWARDS_SPEED_TEST.Degraded
      },
      {
        title: "Poor",
        labels: ["Download: 30Mb+", "Upload: 2Mb+", "Latency: <100"],
        rewardWeight: REWARDS_SPEED_TEST.Poor
      },
      {
        title: "Fail",
        labels: ["Download: <30Mb", "Upload: <2Mb", "Latency: >100"],
        rewardWeight: REWARDS_SPEED_TEST.Fail
      }
    ],
    []
  );

  const totalSpent = useMemo(() => {
    let value = 0;
    if (radioCount.indoor.quantity) {
      value += radioCount.indoor.price * radioCount.indoor.quantity;
    }

    if (radioCount.outdoor.quantity) {
      value += radioCount.outdoor.price * radioCount.outdoor.quantity;
    }
    if (radioCount.highOutdoor.quantity) {
      value += radioCount.highOutdoor.price * radioCount.highOutdoor.quantity;
    }

    return value;
  }, [
    radioCount.highOutdoor.price,
    radioCount.highOutdoor.quantity,
    radioCount.indoor.price,
    radioCount.indoor.quantity,
    radioCount.outdoor.price,
    radioCount.outdoor.quantity
  ]);

  const aroi = useMemo(() => {
    if (!tokenPrice) return 0;

    const diff = calculatedMobileRewardsPerDay * 365 * tokenPrice - totalSpent;

    return (diff / totalSpent) * 100;
  }, [calculatedMobileRewardsPerDay, tokenPrice, totalSpent]);

  const speculateAndRoiCalculate: SpeculationItem[] = useMemo(
    () => [
      {
        title: "Speculation on $MOBILE",
        value: (
          <div className="flex flex-col mt-2">
            <span className="text-sm font-light text-gray-200">Price ($):</span>
            <input
              type="number"
              value={tokenPrice}
              min={0}
              step={0.0001}
              className="mt-2 border rounded px-4 py-2 backdrop-blur-3xl bg-white bg-opacity-10 text-white font-normal"
              onChange={(e) => setTokenPrice(Number(e.target.value))}
            />
          </div>
        )
      },
      {
        title: "Annualized ROI",
        value: `${aroi.toFixed(0)} %`,
        description: "Annualized return on investment"
      },
      {
        title: "Payback time",
        value: `${
          tokenPrice
            ? (
                totalSpent /
                (calculatedMobileRewardsPerDay * tokenPrice)
              ).toFixed(0) + " days"
            : "-"
        }`,
        description: (
          <span>
            $
            {(calculatedMobileRewardsPerDay * 31 * tokenPrice).toLocaleString()}{" "}
            - per month
          </span>
        )
      }
    ],
    [aroi, calculatedMobileRewardsPerDay, tokenPrice, totalSpent]
  );

  const renderLatestNetworkStatsDate = useMemo(() => {
    const latestDataTimestamp = last(fiveGMetrics.outdoorCount)?.timestamp;
    return latestDataTimestamp
      ? ` retrieved on ${moment(latestDataTimestamp).format("DD MMMM")}`
      : null;
  }, [fiveGMetrics.outdoorCount]);

  const handleFaqIndex = useCallback(
    (index: number) => {
      if (openFaqIndex.includes(index)) {
        setOpenFaqIndex((prev) => prev.filter((i) => i !== index));
        return;
      }

      setOpenFaqIndex((prevState) => [...prevState, index]);
    },
    [openFaqIndex]
  );

  const onScroll = useCallback((direction: "left" | "right") => {
    if (direction === "left") {
      containerProductsRef.current?.scrollTo({
        left: containerProductsRef.current?.scrollLeft - 320,
        behavior: "smooth"
      });

      return;
    }

    containerProductsRef.current?.scrollTo({
      left: containerProductsRef.current?.scrollLeft + 320,
      behavior: "smooth"
    });
  }, []);

  const filteredProducts = useMemo(() => {
    if (isEmpty(filteredLabels)) return products;

    return products.filter((product) =>
      isEmpty(difference(filteredLabels, product.labels))
    );
  }, [filteredLabels]);

  return (
    <FilteredListProvider>
      <Helmet>
        <title>Hotspotty - Helium 5G $MOBILE Genesis Rewards Calculator</title>
      </Helmet>
      <AppShell
        {...props}
        className="h-aut  o sm:h-auto"
        currentPage={
          <div className="flex items-center">
            <span>Calculate 5G rewards</span>
          </div>
        }
      >
        <Helmet>
          <title>Hotspotty - 5g Genesis Mobile Rewards Calculator</title>
        </Helmet>

        <img
          src="/5g-background.png"
          className="fixed inset-0 w-full h-full object-cover"
          alt="background"
        />

        <div className="flex items-center flex-col w-full max-w-7xl mx-auto px-4 z-[1] mt-14">
          <div className="w-full">
            <div className="flex items-center justify-between">
              <img src="/5G.png" alt="hotspots" className="hidden lg:flex" />

              <div className="flex flex-col items-start lg:pl-10">
                <span className="font-bold text-4xl lg:text-5xl leading-tight text-hotspotty-400">
                  Helium 5G
                </span>
                <span className="text-white font-bold text-4xl lg:text-5xl leading-tight">
                  $MOBILE Genesis Tokens Arrived!
                </span>
                <span className="text-gray-400 py-4 lg:pt-4 lg:pb-8">
                  It’s time to get rewarded for providing 5G coverage with your
                  Small Cell radios connected to your 5G Helium Hotspots.
                  Estimate the $MOBILE token rewards right now.
                </span>

                <button
                  onClick={() => {
                    calculatorRef.current?.scrollIntoView({
                      behavior: "smooth"
                    });
                  }}
                  className="bg-hotspotty-400 text-white text-sm hover:bg-hotspotty-700 rounded-full py-2 px-16 font-medium transition-ease-in-out duration-200"
                >
                  Start now
                </button>
              </div>
            </div>

            <div className="mt-16 lg:mt-20 flex flex-col">
              <span className="text-white font-bold text-4xl lg:text-5xl">
                <span className="text-hotspotty-400">$MOBILE </span>
                Genesis Rewards Calculator
              </span>

              <span className="text-gray-400 font-light text-base lg:text-lg mt-2">
                The $MOBILE Genesis period started on August 12th. For each
                connected Helium 5G Small Cell Radio you will earn $MOBILE
                tokens until the end of the{" "}
                <ExternalLink
                  className="inline-flex"
                  text="Cellular Summer"
                  url="https://www.helium.com/summer"
                />
                . Afterwards the $MOBILE tokens will be redeemable for $HNT
                tokens.
              </span>

              <div ref={calculatorRef} className="pt-10">
                <div className="flex flex-col">
                  <span className="text-white font-bold text-3xl lg:text-4xl leading-tight">
                    1. Enter radio details
                  </span>
                  <span className="text-gray-400 font-light text-base lg:text-lg mt-2">
                    Pick a combination of radios you would like to simulate
                    rewards for.
                  </span>
                </div>

                <div className="pt-10 grid grid-cols-1 gap-10 lg:gap-20 lg:grid-cols-3 xs:grid-cols-2">
                  {radioMiningCalculate.map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-col justify-center border relative border-gray-300 rounded-xl flex-1 w-full lg:w-auto px-4 pt-10 pb-5 backdrop-blur-3xl bg-white bg-opacity-10"
                    >
                      <span className="text-white absolute top-0 legt-0 p-3 rounded-md bg-hotspotty-400 text-sm font-bold -mt-5">
                        {item.rewardWeight.toFixed(1)}x
                      </span>

                      <div className="flex items-center">
                        <span className="text-white font-bold text-xl">
                          {item.title}
                        </span>
                        {item.tooltip && (
                          <Tooltip placement="top" overlay={item.tooltip}>
                            <InfoIcon className="w-5 ml-1 cursor-pointer" />
                          </Tooltip>
                        )}
                      </div>

                      <span className="text-sm font-light my-4 text-gray-400">
                        {item.description}
                      </span>

                      <div className="flex flex-col">
                        <span className="text-sm font-light text-gray-200">
                          Quantity:
                        </span>
                        <input
                          type="number"
                          value={item.quantity}
                          min={0}
                          className="text-white mt-2 border rounded px-4 py-2 backdrop-blur-3xl bg-white bg-opacity-10"
                          onChange={(e) =>
                            item.setQuantity(Number(e.target.value))
                          }
                        />
                      </div>

                      <div className="flex flex-col mt-5">
                        <span className="text-sm font-light text-gray-200">
                          Price ($):
                        </span>
                        <input
                          type="number"
                          value={item.price}
                          min={0}
                          className="text-white mt-2 border rounded px-4 py-2 backdrop-blur-3xl bg-white bg-opacity-10"
                          onChange={(e) =>
                            item.setPrice(Number(e.target.value))
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col mt-20">
                <div className="flex flex-col">
                  <span className="text-white font-bold text-3xl lg:text-4xl leading-tight">
                    2. Select Quality of Service and simulate rewards
                  </span>
                  <span className="text-gray-400 font-light text-base lg:text-lg mt-2">
                    The Quality of Service is classified based on Speed Test
                    results and rewards are updated accordingly. More info can
                    be found{" "}
                    <ExternalLink
                      className="inline-flex"
                      text="here"
                      url="https://docs.helium.com/5g-on-helium/mobile-poc/#tiered-speed-test"
                    />
                    .
                  </span>
                </div>

                <div className="pt-10 grid grid-cols-1 gap-6 lg:grid-cols-4">
                  {speedTestTier.map((item, index) => (
                    <div
                      key={index}
                      className={classNames(
                        item.title === speedTestSelected
                          ? "border-hotspotty-300"
                          : "border-gray-300",
                        "flex flex-col justify-center border relative rounded-lg flex-1 w-full lg:w-auto pt-10 px-4 py-3 backdrop-blur-3xl bg-white bg-opacity-10 cursor-pointer group"
                      )}
                      onClick={() => setSpeedTestSelected(item.title)}
                    >
                      <span className="text-white absolute top-0 legt-0 p-3 rounded-md bg-hotspotty-400 text-sm font-bold -mt-5">
                        {item.rewardWeight}x
                      </span>

                      <span className="text-white font-bold text-xl mb-2 group-hover:underline">
                        {item.title}
                      </span>

                      {item.labels.map((label) => (
                        <span
                          className={classNames(
                            item.title === speedTestSelected
                              ? "text-gray-200"
                              : "text-gray-400",
                            "text-sm "
                          )}
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              {showResults && (
                <>
                  <span className="text-white font-bold text-2xl mt-6 lg:mt-24">
                    Your $MOBILE Rewards
                  </span>

                  <div className="my-6 border border-gray-300 rounded-xl backdrop-blur-3xl bg-white bg-opacity-10">
                    <div className="py-6 grid grid-cols-1 gap-6 sm:grid-cols-3 xs:grid-cols-2">
                      {mobileMiningEstimate.map((item, index) => (
                        <div
                          key={index}
                          className="flex flex-col justify-center flex-1 w-full lg:w-auto px-6 py-4 lg:py-0"
                        >
                          <div className="flex items-center">
                            <span className="text-white font-bold text-xl">
                              {item.title}
                            </span>
                            {item.tooltip && (
                              <Tooltip placement="top" overlay={item.tooltip}>
                                <InfoIcon className="text-white w-5 ml-1 mt-1 cursor-pointer" />
                              </Tooltip>
                            )}
                          </div>

                          <span className="py-2 text-hotspotty-200 font-bold text-4xl">
                            {item.value}
                          </span>
                          <span className="text-white text-sm">
                            {item.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {!showResults && (
                <button
                  onClick={unlockResults}
                  className={classNames(
                    " text-white text-sm rounded-full py-2 px-20 font-medium mx-auto border border-transparent transition-ease-in-out duration-200 mt-10 mb-12 bg-hotspotty-400 hover:bg-hotspotty-700 lg:mb-24 outline-none focus:outline-none"
                  )}
                >
                  Simulate $MOBILE Rewards
                </button>
              )}

              {showResults && (
                <span className="text-white font-light text-base lg:text-lg">
                  This calculator estimates how many $MOBILE tokens you could
                  earn on a daily, weekly, and monthly basis based on current
                  network stats{renderLatestNetworkStatsDate}. Expect this
                  number to decrease with more Radios being installed by the
                  Helium Community.
                </span>
              )}

              {showRoi && (
                <div className="w-full mt-12">
                  <span className="text-white font-bold text-2xl">
                    Speculate and calculate ROI
                  </span>

                  <div className="py-6 grid grid-cols-1 gap-6 sm:grid-cols-3 xs:grid-cols-2">
                    {speculateAndRoiCalculate.map((item, index) => (
                      <div
                        key={index}
                        className="flex flex-col border border-gray-300 rounded-xl flex-1 w-full lg:w-auto px-6 py-6 min-h-[140px] lg:min-h-[169px] backdrop-blur-3xl bg-white bg-opacity-10"
                      >
                        <div className="flex items-center">
                          <span className="text-white font-bold text-xl">
                            {item.title}
                          </span>
                          {item.tooltip && (
                            <Tooltip placement="top" overlay={item.tooltip}>
                              <InfoIcon className="text-white w-5 ml-1 mt-1 cursor-pointer" />
                            </Tooltip>
                          )}
                        </div>

                        <span className="py-2 text-hotspotty-200 font-bold text-4xl">
                          {item.value}
                        </span>

                        <span className="text-white text-sm">
                          {item.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {showResults && (
                <button
                  onClick={toggleShowROI}
                  className={classNames(
                    " text-white text-sm rounded-full py-2 px-20 font-medium mx-auto border border-transparent transition-ease-in-out duration-200 mt-10 mb-12 lg:mb-24 outline-none focus:outline-none",
                    showRoi
                      ? "bg-transparent border-white bg-white bg-opacity-20 hover:bg-hotspotty-400 backdrop-blur-3xl"
                      : "bg-hotspotty-400 hover:bg-hotspotty-700"
                  )}
                >
                  {showRoi
                    ? "Hide ROI Calculator"
                    : "Speculate and calculate ROI"}
                </button>
              )}
            </div>

            <div className="mt-10 mb-12">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                <span className="text-white font-bold text-4xl lg:text-5xl leading-tight">
                  Start mining right now
                </span>
                <div className="labels sm:-mx-1 flex justify-between flex-wrap mt-6 sm:mt-0">
                  {[indoorRadioLabel, outdoorRadioLabel, minerLabel].map(
                    (label) => (
                      <span
                        key={label.name}
                        onClick={() => {
                          setFilteredLabels((prevFilteredLabels) =>
                            prevFilteredLabels.includes(label)
                              ? without(prevFilteredLabels, label)
                              : prevFilteredLabels.concat(label)
                          );
                        }}
                        className={classNames(
                          filteredLabels.includes(label)
                            ? label.className
                            : "backdrop-blur-3xl bg-white bg-opacity-10 border-white",
                          label.hoverClassName,
                          "transition-all focus:outline-none border px-3 text-sm rounded-full mx-1 cursor-pointer sm:hover:bg-opacity-60 py-0.5 my-2"
                        )}
                      >
                        {label.name}
                      </span>
                    )
                  )}
                </div>
              </div>

              {isEmpty(filteredProducts) ? (
                <div>No products match your filter criterea.</div>
              ) : (
                <div className="flex items-center xl:-mx-12">
                  <button
                    className="hidden lg:flex p-3 mr-4 items-center justify-center backdrop-blur-3xl bg-white bg-opacity-10 rounded-full border border-white hover:bg-hotspotty-400 transition-all focus:outline-none"
                    onClick={() => onScroll("left")}
                  >
                    <ArrowLeftIcon className="w-5 text-white" />
                  </button>

                  <div
                    ref={containerProductsRef}
                    className="products-5g horizontal-snap-scroll flex"
                  >
                    {filteredProducts.map((item, key) => (
                      <a
                        href={item.link}
                        key={key}
                        target="_blank"
                        className="relative horizontal-snap-scroll-child flex flex-col w-[280px] min-w-[280px] max-w-[280px] mr-10 rounded-lg overflow-hidden border border-gray-300 bg-white cursor-pointer hover:border-hotspotty-200 transition-ease-in-out duration-200 group"
                        rel="noreferrer"
                      >
                        <span className="absolute text-gray-100 bg-gray-800 top-2 left-2 px-3 text-sm rounded-full mx-1">
                          {item.vendor}
                        </span>

                        <img src={item.image} alt="product" />

                        <div className="flex items-start flex-col p-4 bg-gray-800 h-full">
                          <div className="labels -mx-1 flex flex-wrap">
                            {item.labels.map(({ name, className }) => (
                              <span
                                key={name}
                                className={classNames(
                                  className,
                                  "px-3 text-sm rounded-full mx-1"
                                )}
                              >
                                {name}
                              </span>
                            ))}
                          </div>
                          <span className="text-white text-lg font-bold my-3">
                            {item.price}
                          </span>
                          <span className="text-gray-300 text-sm font-light group-hover:underline">
                            {item.description}
                          </span>
                        </div>
                      </a>
                    ))}
                  </div>

                  <button
                    className="hidden lg:flex ml-4 p-3 items-center justify-center backdrop-blur-3xl bg-white bg-opacity-10 rounded-full border border-white hover:bg-hotspotty-400 transition-all focus:outline-none"
                    onClick={() => onScroll("right")}
                  >
                    <ArrowRightIcon className="w-5 text-white" />
                  </button>
                </div>
              )}
            </div>

            <div className="mt-16 lg:mt-20 mb-8 lg:mb-16">
              <span className="text-white font-bold text-4xl lg:text-5xl">
                Helium 5G Statistics
              </span>

              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3 xs:grid-cols-2">
                {helium5GStats.map((item, index) => (
                  <div
                    key={index}
                    className="flex flex-col justify-center border border-gray-300 rounded-xl flex-1 w-full lg:w-auto px-6 py-4 lg:py-0 min-h-[140px] lg:min-h-[169px] backdrop-blur-3xl bg-white bg-opacity-10"
                  >
                    <div className="flex items-center">
                      <span className="text-white font-bold text-xl">
                        {item.title}
                      </span>
                      {item.tooltip && (
                        <Tooltip placement="top" overlay={item.tooltip}>
                          <InfoIcon className="text-white w-5 ml-1 cursor-pointer" />
                        </Tooltip>
                      )}
                    </div>

                    <span className="py-2 text-hotspotty-200 font-bold text-4xl">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-4 lg:mb-10 relative">
              <span className="text-white font-bold text-2xl">
                Deployed 5G Radios
              </span>

              <div className="w-full h-[320px]">
                <span className="text-white text-xs font-semibold absolute right-0 mr-10 uppercase">
                  {radiosChartData.length > 0 &&
                    `Last ${radiosChartData.length} days`}
                </span>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    width={500}
                    height={400}
                    data={radiosChartData}
                    margin={{
                      top: 10,
                      right: 30,
                      left: 0,
                      bottom: 0
                    }}
                  >
                    <XAxis
                      dataKey="name"
                      color="#ffffff0"
                      style={{ display: "none" }}
                    />
                    <RechartsTooltip contentStyle={{ color: "black" }} />
                    <Area
                      type="monotone"
                      dataKey="Indoor"
                      stackId="1"
                      opacity={0.6}
                      stroke="#9551C5"
                      fill="#9551C5"
                    />
                    <Area
                      type="monotone"
                      dataKey="Outdoor"
                      stackId="2"
                      opacity={0.6}
                      stroke="#4F1254"
                      fill="#4F1254"
                    />
                    <Area
                      type="monotone"
                      dataKey="High-Gain Outdoor"
                      stackId="3"
                      opacity={0.6}
                      stroke="#9f45a7"
                      fill="#9f45a7"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="flex items-center">
                <div className="flex items-center">
                  <span className="bg-[#9551C5] w-3 h-3 lg:w-7 lg:h-7 rounded-full flex-shrink-0 flex"></span>
                  <span className="text-white text-xs lg:text-sm font-bold ml-2 lg:ml-4 uppercase">
                    Indoor radios
                  </span>
                </div>

                <div className="flex items-center mx-2 lg:mx-8">
                  <span className="bg-[#4F1254] w-3 h-3 lg:w-7 lg:h-7 rounded-full flex-shrink-0 flex"></span>
                  <span className="text-white text-xs lg:text-sm font-bold ml-2 lg:ml-4 uppercase">
                    Outdoor radios
                  </span>
                </div>

                <div className="flex items-center">
                  <span className="bg-[#9f45a7] w-3 h-3 lg:w-7 lg:h-7 rounded-full flex-shrink-0 flex"></span>
                  <span className="text-white text-xs lg:text-sm font-bold ml-2 lg:ml-4 uppercase">
                    High-Gain Outdoor Radios
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-4 lg:mb-10 relative">
              <span className="text-white font-bold text-2xl">
                Cities with 5G radios
              </span>

              <div className="w-full h-[320px]">
                <span className="text-white text-xs font-semibold absolute right-0 mr-10 uppercase">
                  {citiesChartData.length > 0 &&
                    `Last ${citiesChartData.length} days`}
                </span>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    width={500}
                    height={400}
                    data={citiesChartData}
                    margin={{
                      top: 10,
                      right: 30,
                      left: 0,
                      bottom: 0
                    }}
                  >
                    <XAxis
                      dataKey="name"
                      color="#ffffff0"
                      style={{ display: "none" }}
                    />
                    <RechartsTooltip contentStyle={{ color: "black" }} />
                    <Area
                      type="monotone"
                      dataKey="Cities"
                      stackId="1"
                      opacity={0.6}
                      stroke="#51c55d"
                      fill="#51c55d"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="flex items-center">
                <div className="flex items-center">
                  <span className="bg-[#51c55d] w-3 h-3 lg:w-7 lg:h-7 rounded-full flex-shrink-0 flex"></span>
                  <span className="text-white text-xs lg:text-sm font-bold ml-2 lg:ml-4 uppercase">
                    Cities
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-12 lg:mt-32">
              <span className="text-white font-bold text-4xl lg:text-5xl">
                Frequently Asked Questions
              </span>

              <div className="flex flex-col mt-6 lg:mt-10">
                {faq.map((item, index) => (
                  <div
                    key={index}
                    className={classNames(
                      "flex flex-col border-b border-gray-400 pt-3 last:border-b-0"
                    )}
                  >
                    <div
                      onClick={() => handleFaqIndex(index)}
                      className="flex items-center justify-between cursor-pointer pb-8 hover:text-hotspotty-200 group transition-ease-in-out duration-200"
                    >
                      <span className="text-white font-semibold text-xl lg:text-2xl">
                        {item.title}
                      </span>
                      {openFaqIndex.includes(index) ? (
                        <ChevronUpIcon className="text-white w-8 group-hover:text-hotspotty-200" />
                      ) : (
                        <ChevronDownIcon className="text-white w-8 group-hover:text-hotspotty-200" />
                      )}
                    </div>

                    {openFaqIndex.includes(index) && (
                      <span className="text-gray-300 font-medium pb-4 -mt-5 text-lg">
                        {item.content}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </AppShell>
    </FilteredListProvider>
  );
};

export default Calculate5GRewards;

//
// Utils
//

interface RadioCountItem {
  quantity: number;
  price: number;
}

interface RadioCount {
  indoor: RadioCountItem;
  outdoor: RadioCountItem;
  highOutdoor: RadioCountItem;
}

interface CardStats {
  title: string;
  value: string | JSX.Element;
  tooltip?: string;
  description?: string;
  type?: string;
}

interface Metric {
  key: string;
  timestamp: number;
  value: number;
}

interface FiveGMetrics {
  citiesCount: Metric[];
  count: Metric[];
  indoorCount: Metric[];
  outdoorCount: Metric[];
  statesCount: Metric[];
}

interface MobileTools {
  total: number;
  total_tx: number;
  wallets_count: number;
  payment_hashes: string[];
}

interface Label {
  name: string;
  className: string;
  hoverClassName: string;
}

interface Product {
  price: string;
  description: string;
  image: string;
  link: string;
  vendor: Vendor;
  labels: Label[];
}

interface RadioMiningCalculate {
  title: string;
  description: string;
  tooltip?: string;
  quantity: number;
  setQuantity: (value: number) => void;
  price: number;
  setPrice: (value: number) => void;
  rewardWeight: number;
}

interface SpeculationItem {
  title: string;
  description?: any;
  tooltip?: string | Element;
  value: any;
}

const DAILY_MOBILE_REWARDS = 100_000_000;

const REWARD_WEIGHT_INDOOR_RADIO = 1.0;

const REWARD_WEIGHT_OUTDOOR_RADIO = 2.5;

const REWARD_WEIGHT_HIGH_POWER_OUTDOOR_RADIO = 4.0;

const HIGH_POWER_OURDOUR_RADIO_COUNT = 5;

const REWARDS_SPEED_TEST: { [key: string]: number } = {
  Acceptable: 1,
  Degraded: 0.5,
  Poor: 0.25,
  Fail: 0
};

const formatNumber = (number: number) => {
  if (number > 1e6) {
    return `${(number / 1e6).toFixed(2)}M`;
  }

  if (number > 1e3) {
    return `${(number / 1e3).toFixed(2)}K`;
  }

  return number.toFixed(0).toLocaleString();
};

const getLastValue = (counts: Metric[]) => last(counts)?.value || 0;

const faq = [
  {
    title:
      "What do existing FreedomFi Hotspot owners need to do to start mining $MOBILE?",
    content: (
      <span>
        In order to mine $MOBILE, your FreedomFi Hotspot should be activated and
        upgraded to the latest firmware. Make sure your hotspot is properly
        attached to an indoor/outdoor cell. More detailed instructions can be
        found{" "}
        <a
          href="https://freedomfi.com/freedomfi-gateway-setup-guide"
          target="_blank"
          className="text-hotspotty-200 underline"
          rel="noreferrer"
        >
          here
        </a>
        .
      </span>
    )
  },
  {
    title: "What is MOBILE Genesis?",
    content:
      "MOBILE Genesis is the bootstrapping phase of the Helium 5G network. 5G Hotspot bundle owners will need to stay connected to provide coverage to earn $MOBILE reward starting early summer."
  },
  {
    title: "When will we start earning $MOBILE for Proof-of-Coverage?",
    content:
      "Mobile Genesis is the phase between Mobile mining launch and launch of Treasury that backs $MOBILE with $HNT. The 5G Proof-of-Coverage mechanism will be launched afterwards, with corresponding $MOBILE rewards."
  },
  {
    title: "Will I still earn for Data Transfer on Helium 5G network?",
    content:
      "Yes, rewards for data transfer will be activated at a later phase."
  },
  {
    title:
      "Can I host a Helium 5G Hotspot if I am residing outside of the U.S.?",
    content:
      "The Helium 5G network is currently only available in the U.S. This is due to public access to the CBRS spectrum, which is not available in other countries."
  },
  {
    title: "When can I start using the Helium 5G network on my phone?",
    content:
      "You can start using the Helium 5G network now using FreedomFi's sim card. More network operators will join Helium network soon. Stay tuned!"
  },
  {
    title:
      "Where can I get more information about Helium Mobile subDAO and its governance?",
    content: (
      <span className="flex flex-col">
        <span>
          Read{" "}
          <a
            href="https://github.com/helium/HIP/blob/main/0053-mobile-dao.md"
            target="_blank"
            className="text-hotspotty-200 underline"
            rel="noreferrer"
          >
            Mobile subDAO
          </a>{" "}
          proposal
        </span>
        <span>
          Join{" "}
          <a
            href="https://discord.com/channels/404106811252408320"
            target="_blank"
            className="text-hotspotty-200 underline"
            rel="noreferrer"
          >
            HIP53
          </a>{" "}
          Channel on{" "}
          <a
            href="https://discord.com/invite/helium"
            target="_blank"
            className="text-hotspotty-200 underline"
            rel="noreferrer"
          >
            Discord
          </a>
        </span>
        <span>
          Listen to the{" "}
          <a
            href="https://twitter.com/i/spaces/1OyKADQWppMxb?s=20"
            target="_blank"
            className="text-hotspotty-200 underline"
            rel="noreferrer"
          >
            Twitter Spaces
          </a>{" "}
          with new networks: 5G, WiFi, VPN
        </span>
        <span>
          Watch the Hotspot Podcast's recording of{" "}
          <a
            href="https://www.youtube.com/watch?v=9OTr5ILAEiM&ab_channel=TheHotspot%C2%B7HeliumNetworkPodcast"
            target="_blank"
            className="text-hotspotty-200 underline"
            rel="noreferrer"
          >
            Discord AMAs
          </a>{" "}
          on HIP51-53
        </span>
      </span>
    )
  }
];

enum Vendor {
  parleyLabs = "Parley Labs",
  calchipConnect = "Calchip Connect",
  mosoLabs = "MosoLabs"
}

const indoorRadioLabel = {
  name: "Indoor radio",
  className: "text-blue-300 bg-blue-900 bg-opacity-50 border-blue-300",
  hoverClassName: "sm:hover:bg-blue-900"
};

const outdoorRadioLabel = {
  name: "Outdoor radio",
  className: "text-pink-300 bg-pink-900 bg-opacity-50 border-pink-300",
  hoverClassName: "sm:hover:bg-pink-900"
};

const minerLabel = {
  name: "Miner",
  className: "text-indigo-300 bg-indigo-900 bg-opacity-50 border-indigo-300",
  hoverClassName: "sm:hover:bg-indigo-900"
};

const products: Product[] = [
  {
    price: "$2,499.00",
    description: "Baicell NOVA 430H + FreedomFi 5G Helium Hotspot Miner Bundle",
    image: "/5g-products/BaicellNOVA+freedom.png",
    link: "https://shop.parleylabs.com/collections/helium-5g/products/nova-430h#a_aid=Hotspotty",
    vendor: Vendor.parleyLabs,
    labels: [outdoorRadioLabel, minerLabel]
  },
  {
    price: "$1,599.00",
    description: "Baicell NOVA 430H (Small Cell Only)",
    image: "/5g-products/BaicellNOVA.png",
    link: "https://shop.parleylabs.com/collections/helium-5g/products/baicell-nova-430h-small-cell-only#a_aid=Hotspotty",
    vendor: Vendor.parleyLabs,
    labels: [outdoorRadioLabel]
  },
  {
    price: "$1,795.00",
    description: "FreedomFi Helium 5G Indoor Bundle - FREE SHIPPING",
    image: "/5g-products/FreedomFiSmallCellBundle.webp",
    link: "https://www.calchipconnect.com/products/freedomfi-helium-5g-indoor-bundle?ref=hotspotty",
    vendor: Vendor.calchipConnect,
    labels: [indoorRadioLabel, minerLabel]
  },
  {
    price: "$999.00",
    description: "FreedomFi Helium 5G Indoor CBRS Small Cell – FreedomFi One",
    image: "/5g-products/FreedomFiIndoorSmallCell.webp",
    link: "https://www.calchipconnect.com/products/freedomfi-helium-5g-indoor-cbrs-small-cell?ref=hotspotty",
    vendor: Vendor.calchipConnect,
    labels: [indoorRadioLabel]
  },
  {
    price: "$999.99",
    description: "FreedomFi Helium 5G Gateway US915 Miner",
    image:
      "/5g-products/FreedomFi-2_1024x1024_cd9e1bf4-c2a5-4a52-b940-117cfa888602_1024x1024@2x.webp",
    link: "https://shop.parleylabs.com/collections/helium-5g/products/freedomfi-helium-5g-gateway-us915-miner#a_aid=Hotspotty",
    vendor: Vendor.parleyLabs,
    labels: [minerLabel]
  },
  {
    price: "$2,499.00",
    description: "FreedomFi Helium 5G Outdoor Bundle (Gateway & Small Cell)",
    image:
      "/5g-products/FreedomFiOutdoorSmallCellBundle_21cfd9f0-7c13-4259-9ce8-b2e0d55222bd_750x750.webp",
    link: "https://www.calchipconnect.com/products/freedomfi-helium-5g-outdoor-bundle?ref=hotspotty",
    vendor: Vendor.calchipConnect,
    labels: [outdoorRadioLabel, minerLabel]
  },
  {
    price: "$3,200.00",
    description: "MOBILE Genesis Indoor Bundle",
    image: "/5g-products/2xENGLEWOODIndoor_FreedomFi1080x1080_1080x.webp",
    link: "https://mosolabs.com/products/mobile-genesis-indoor-bundle?ref=eOGg146y",
    vendor: Vendor.mosoLabs,
    labels: [indoorRadioLabel, minerLabel]
  },
  {
    price: "$1,800.00",
    description: "Indoor CBRS Small Cell + FreedomFi Gateway Bundle",
    image:
      "/5g-products/ENGLEWOOD_MOSO-FREEFi_1080x1080_96948dd2-e2f8-4289-af28-a0874e67371b_1080x.webp",
    link: "https://mosolabs.com/products/indoor-freedomfi-bundle?ref=eOGg146y",
    vendor: Vendor.mosoLabs,
    labels: [indoorRadioLabel, minerLabel]
  },
  {
    price: "$1,500.00",
    description: "Indoor CBRS Small Cell",
    image: "/5g-products/ENGLEWOOD_MOSO1080x1080_1080x.webp",
    link: "https://mosolabs.com/products/indoor-small-cell?ref=eOGg146y",
    vendor: Vendor.mosoLabs,
    labels: [indoorRadioLabel]
  }
];
